import React, { useState, useRef, useEffect } from 'react';
import { 
  View, 
  Text, 
  TouchableOpacity, 
  StyleSheet, 
  Platform, 
  Animated, 
  Easing,
  Dimensions,
  ScrollView,
  Modal,
  PanResponder
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import { Ionicons } from '@expo/vector-icons';
import { useBackgroundQuery, QueryStatus } from '../context/BackgroundQueryContext';
import { useNavigation } from '../context/NavigationContext';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// Animated Progress Bar with shimmer effect
const AnimatedProgressBar = ({ progress, isProcessing }) => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(0.3)).current;
  
  // Continuous shimmer animation
  useEffect(() => {
    if (isProcessing) {
      const shimmer = Animated.loop(
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 1500,
          easing: Easing.linear,
          useNativeDriver: true
        })
      );
      shimmer.start();
      
      // Also pulse the opacity when progress is low
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 800,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true
          }),
          Animated.timing(pulseAnim, {
            toValue: 0.5,
            duration: 800,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true
          })
        ])
      );
      pulse.start();
      
      return () => {
        shimmer.stop();
        pulse.stop();
      };
    }
  }, [isProcessing]);
  
  const shimmerTranslate = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-100, 200]
  });
  
  // When progress is very low, show indeterminate animation
  const effectiveProgress = progress < 10 ? Math.max(progress, 30) : progress;
  
  return (
    <View style={progressStyles.container}>
      <View style={progressStyles.bar}>
        {/* Base progress fill */}
        <Animated.View 
          style={[
            progressStyles.fill, 
            { 
              width: `${effectiveProgress}%`,
              opacity: progress < 10 ? pulseAnim : 1
            }
          ]}
        >
          <LinearGradient
            colors={['#8b5cf6', '#a78bfa', '#c4b5fd']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={StyleSheet.absoluteFill}
          />
        </Animated.View>
        
        {/* Shimmer overlay */}
        {isProcessing && (
          <Animated.View
            style={[
              progressStyles.shimmer,
              {
                transform: [{ translateX: shimmerTranslate }]
              }
            ]}
          >
            <LinearGradient
              colors={['transparent', 'rgba(255,255,255,0.3)', 'transparent']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={StyleSheet.absoluteFill}
            />
          </Animated.View>
        )}
      </View>
    </View>
  );
};

const progressStyles = StyleSheet.create({
  container: {
    marginTop: 4,
  },
  bar: {
    height: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 2,
    overflow: 'hidden',
    position: 'relative',
  },
  fill: {
    height: '100%',
    borderRadius: 2,
    overflow: 'hidden',
  },
  shimmer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: 100,
  }
});

// Elapsed time display component with live updating
const ElapsedTimeDisplay = ({ startTime }) => {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    if (!startTime) return;
    
    const updateElapsed = () => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    };
    
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [startTime]);
  
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <Text style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: 11, fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier' }}>
      {formatTime(elapsed)}
    </Text>
  );
};

// Detect mobile screen
const isMobile = SCREEN_WIDTH < 768;

const BackgroundQueryModal = () => {
  const {
    activeQueries,
    isMinimized,
    setIsMinimized,
    cancelQuery,
    removeQuery,
    markQueryViewed,
    getUnviewedCount,
    clearCompletedQueries,
    QueryStatus,
    modalPosition,
    toggleModalPosition,
    IS_MOBILE
  } = useBackgroundQuery();
  
  const { navigateTo } = useNavigation();

  // Animation values
  const slideAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const expandAnim = useRef(new Animated.Value(0)).current;
  const positionAnim = useRef(new Animated.Value(modalPosition === 'bottom-right' ? 0 : 1)).current;
  
  // Cancel confirmation modal
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  
  // Mobile bottom sheet state
  const [showMobileSheet, setShowMobileSheet] = useState(false);
  
  // Unviewed count
  const unviewedCount = getUnviewedCount();

  // Animate position change
  useEffect(() => {
    if (activeQueries.length === 0) return;
    Animated.spring(positionAnim, {
      toValue: modalPosition === 'bottom-right' ? 0 : 1,
      tension: 60,
      friction: 10,
      useNativeDriver: false
    }).start();
  }, [modalPosition, activeQueries.length]);

  // Animate slide in on mount
  useEffect(() => {
    if (activeQueries.length === 0) return;
    Animated.spring(slideAnim, {
      toValue: 1,
      tension: 50,
      friction: 8,
      useNativeDriver: true
    }).start();
  }, [activeQueries.length]);

  // Pulse animation for unviewed notifications
  useEffect(() => {
    if (activeQueries.length === 0) return;
    if (unviewedCount > 0 && isMinimized) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.1,
            duration: 500,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 500,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true
          })
        ])
      );
      pulse.start();
      return () => pulse.stop();
    }
  }, [unviewedCount, isMinimized, activeQueries.length]);

  // Expand/collapse animation
  useEffect(() => {
    if (activeQueries.length === 0) return;
    Animated.spring(expandAnim, {
      toValue: isMinimized ? 0 : 1,
      tension: 60,
      friction: 10,
      useNativeDriver: false
    }).start();
  }, [isMinimized, activeQueries.length]);

  // Don't render if no active queries
  if (activeQueries.length === 0) {
    return null;
  }

  // Toggle minimize state
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    
    // Mark all as viewed when expanding
    if (isMinimized) {
      activeQueries
        .filter(q => q.status === QueryStatus.COMPLETED)
        .forEach(q => markQueryViewed(q.id));
    }
  };

  // Handle view results - with navigation fix for same-route navigation
  const handleViewResults = (query) => {
    markQueryViewed(query.id);
    
    // Store results in session for SgpFlow to pick up
    if (Platform.OS === 'web') {
      try {
        window.sessionStorage.setItem('sgp:lastQuery', query.prompt);
        window.sessionStorage.setItem('sgp:backgroundResults', JSON.stringify(query.results));
        window.sessionStorage.setItem('sgp:fromBackground', 'true');
        // Add a unique key to force re-render
        window.sessionStorage.setItem('sgp:queryId', query.id);
      } catch (e) {
        console.error('Failed to save background results:', e);
      }
    }
    
    // Use a unique timestamp to force navigation even to the same route
    const navigationKey = `bg_${query.id}_${Date.now()}`;
    
    // Check if we're already on the results page
    const currentPath = Platform.OS === 'web' ? window.location.pathname : '';
    const isOnResultsPage = currentPath.includes('/results');
    
    if (isOnResultsPage) {
      // If already on results page, need to force a reload/re-render
      // Update URL params directly to trigger route change detection
      if (Platform.OS === 'web') {
        const newUrl = `/results?q=${encodeURIComponent(query.prompt)}&bg=${navigationKey}`;
        window.history.pushState({}, '', newUrl);
        // Dispatch a custom event that SgpFlowScreen can listen for
        window.dispatchEvent(new CustomEvent('backgroundQueryNavigation', { 
          detail: { queryId: query.id, prompt: query.prompt } 
        }));
      }
    }
    
    navigateTo('SgpFlow', { 
      payload: { 
        query: query.prompt, 
        fromBackground: true,
        queryId: query.id,
        navigationKey 
      } 
    });
  };

  // Handle cancel - shows confirmation for in-progress, removes immediately for others
  const handleCancelOrRemove = (queryId) => {
    const query = activeQueries.find(q => q.id === queryId);
    if (query?.status === QueryStatus.PROCESSING || query?.status === QueryStatus.PENDING) {
      // Show confirmation for in-progress queries
      setConfirmCancelId(queryId);
    } else {
      // Remove completed/error/cancelled queries immediately
      removeQuery(queryId);
    }
  };

  // Confirm cancel - clear state FIRST to close modal immediately
  const confirmCancel = () => {
    const idToCancel = confirmCancelId;
    // Clear state immediately to close modal
    setConfirmCancelId(null);
    
    // Then perform the cancellation after a microtask to ensure modal closes
    if (idToCancel) {
      setTimeout(() => {
        cancelQuery(idToCancel);
        removeQuery(idToCancel);
      }, 0);
    }
  };
  
  // Dismiss cancel modal
  const dismissCancelModal = () => {
    setConfirmCancelId(null);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case QueryStatus.COMPLETED:
        return '#4ade80';
      case QueryStatus.ERROR:
        return '#f87171';
      case QueryStatus.CANCELLED:
        return '#94a3b8';
      case QueryStatus.PROCESSING:
        return '#60a5fa';
      default:
        return '#fbbf24';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case QueryStatus.COMPLETED:
        return 'checkmark-circle';
      case QueryStatus.ERROR:
        return 'alert-circle';
      case QueryStatus.CANCELLED:
        return 'close-circle';
      case QueryStatus.PROCESSING:
        return 'sync';
      default:
        return 'time';
    }
  };

  // Calculate animated height
  const animatedHeight = expandAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [56, Math.min(400, 80 + activeQueries.length * 90)]
  });

  // Calculate animated width (for mobile)
  const animatedWidth = expandAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [56, isMobile ? SCREEN_WIDTH - 32 : 380]
  });

  // Animated horizontal position
  const animatedRight = positionAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [24, SCREEN_WIDTH - (isMobile ? SCREEN_WIDTH - 32 + 24 : 380 + 24)]
  });
  
  const animatedLeft = positionAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [SCREEN_WIDTH - (isMobile ? SCREEN_WIDTH - 32 + 24 : 380 + 24), 24]
  });

  // Animated styles
  const containerStyle = {
    transform: [
      { 
        translateY: slideAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [200, 0]
        })
      },
      { scale: isMinimized && unviewedCount > 0 ? pulseAnim : 1 }
    ],
    height: animatedHeight,
    width: animatedWidth,
    // Use left position for easier animation
    left: modalPosition === 'bottom-left' ? 24 : undefined,
    right: modalPosition === 'bottom-right' ? 24 : undefined
  };

  // Mobile: show just a floating indicator button
  if (IS_MOBILE && isMinimized && !showMobileSheet) {
    return (
      <>
        <Animated.View 
          style={[
            styles.mobileIndicator,
            {
              transform: [
                { 
                  translateY: slideAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [100, 0]
                  })
                },
                { scale: unviewedCount > 0 ? pulseAnim : 1 }
              ],
              right: modalPosition === 'bottom-right' ? 16 : undefined,
              left: modalPosition === 'bottom-left' ? 16 : undefined
            }
          ]}
        >
          <TouchableOpacity 
            style={styles.mobileIndicatorButton}
            onPress={() => setShowMobileSheet(true)}
            activeOpacity={0.8}
          >
            <LinearGradient
              colors={['#8b5cf6', '#6d28d9']}
              style={styles.mobileIndicatorGradient}
            >
              <Ionicons 
                name={activeQueries.some(q => q.status === QueryStatus.PROCESSING) ? 'sync' : 'layers'} 
                size={22} 
                color="#fff" 
              />
              {unviewedCount > 0 && (
                <View style={styles.mobileIndicatorBadge}>
                  <Text style={styles.badgeText}>{unviewedCount}</Text>
                </View>
              )}
              {activeQueries.some(q => q.status === QueryStatus.PROCESSING) && (
                <Animated.View style={[styles.processingRing]} />
              )}
            </LinearGradient>
          </TouchableOpacity>
        </Animated.View>

        {/* Mobile Bottom Sheet */}
        <Modal
          visible={showMobileSheet}
          transparent
          animationType="slide"
          onRequestClose={() => setShowMobileSheet(false)}
        >
          <TouchableOpacity 
            style={styles.mobileSheetOverlay}
            activeOpacity={1}
            onPress={() => setShowMobileSheet(false)}
          >
            <View style={styles.mobileSheet}>
              {/* Handle bar */}
              <View style={styles.mobileSheetHandle} />
              
              {/* Header */}
              <View style={styles.mobileSheetHeader}>
                <Text style={styles.mobileSheetTitle}>
                  Background Queries ({activeQueries.length})
                </Text>
                <TouchableOpacity onPress={() => setShowMobileSheet(false)}>
                  <Ionicons name="close" size={24} color="rgba(255,255,255,0.7)" />
                </TouchableOpacity>
              </View>

              {/* Queries list */}
              <ScrollView style={styles.mobileSheetContent}>
                {activeQueries.map((query, index) => (
                  <View key={query.id} style={styles.queryCard}>
                    {/* Query header */}
                    <View style={styles.queryHeader}>
                      <View style={styles.queryTitleRow}>
                        <Ionicons 
                          name={getStatusIcon(query.status)} 
                          size={16} 
                          color={getStatusColor(query.status)} 
                        />
                        <Text style={styles.queryPrompt} numberOfLines={2}>
                          {query.prompt || 'Query'}
                        </Text>
                      </View>
                      
                      <TouchableOpacity 
                        onPress={() => handleCancelOrRemove(query.id)}
                        style={styles.closeButton}
                      >
                        <Ionicons name="close" size={16} color="rgba(255,255,255,0.5)" />
                      </TouchableOpacity>
                    </View>

                    {/* Progress bar - animated */}
                    {(query.status === QueryStatus.PENDING || query.status === QueryStatus.PROCESSING) && (
                      <View style={styles.progressContainer}>
                        <AnimatedProgressBar 
                          progress={query.progress || 0} 
                          isProcessing={query.status === QueryStatus.PROCESSING}
                        />
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <Text style={styles.progressText}>
                              {query.progressMessage || 'Querying odds data...'}
                            </Text>
                            {query.startTime && (
                              <ElapsedTimeDisplay startTime={query.startTime} />
                            )}
                          </View>
                          <TouchableOpacity 
                            onPress={() => setConfirmCancelId(query.id)}
                            style={{ paddingVertical: 4, paddingHorizontal: 8 }}
                          >
                            <Text style={{ color: '#f87171', fontSize: 12, fontWeight: '600' }}>Cancel</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}

                    {/* Results / Actions */}
                    {query.status === QueryStatus.COMPLETED && (
                      <View style={styles.resultsRow}>
                        <Text style={styles.resultsText}>
                          {query.sgpCount > 0 
                            ? `${query.sgpCount} SGPs` 
                            : `${query.results?.length || 0} results`}
                        </Text>
                        <TouchableOpacity 
                          style={styles.viewButton}
                          onPress={() => {
                            setShowMobileSheet(false);
                            handleViewResults(query);
                          }}
                        >
                          <Text style={styles.viewButtonText}>View</Text>
                          <Ionicons name="arrow-forward" size={14} color="#fff" />
                        </TouchableOpacity>
                      </View>
                    )}

                    {/* Error state */}
                    {query.status === QueryStatus.ERROR && (
                      <View style={styles.errorRow}>
                        <Ionicons name="warning" size={14} color="#f87171" />
                        <Text style={styles.errorText} numberOfLines={1}>
                          {query.error || 'Query failed'}
                        </Text>
                      </View>
                    )}
                  </View>
                ))}
              </ScrollView>
            </View>
          </TouchableOpacity>
        </Modal>
      </>
    );
  }

  return (
    <>
      <Animated.View style={[styles.container, containerStyle]}>
        {/* Glassmorphism background */}
        <BlurView 
          intensity={80} 
          tint="dark" 
          style={styles.blurBackground}
        >
          <LinearGradient
            colors={['rgba(30, 30, 40, 0.95)', 'rgba(20, 20, 30, 0.98)']}
            style={styles.gradientOverlay}
          />
        </BlurView>

        {/* Header / Minimized state */}
        <TouchableOpacity 
          style={styles.header} 
          onPress={toggleMinimize}
          activeOpacity={0.8}
        >
          <View style={styles.headerLeft}>
            {isMinimized ? (
              <>
                <View style={[styles.statusDot, { backgroundColor: getStatusColor(activeQueries[0]?.status) }]} />
                {unviewedCount > 0 && (
                  <View style={styles.badge}>
                    <Text style={styles.badgeText}>{unviewedCount}</Text>
                  </View>
                )}
              </>
            ) : (
              <>
                <Ionicons name="layers" size={18} color="#c4b5fd" />
                <Text style={styles.headerTitle}>
                  Background Queries ({activeQueries.length})
                </Text>
              </>
            )}
          </View>
          
          <View style={styles.headerRight}>
            {/* Position toggle button - only show when expanded on desktop */}
            {!isMinimized && !isMobile && (
              <TouchableOpacity 
                onPress={toggleModalPosition} 
                style={styles.positionButton}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons 
                  name={modalPosition === 'bottom-right' ? 'chevron-back' : 'chevron-forward'} 
                  size={14} 
                  color="rgba(255,255,255,0.6)" 
                />
              </TouchableOpacity>
            )}
            
            <TouchableOpacity onPress={toggleMinimize} style={styles.chevronButton}>
              <Ionicons 
                name={isMinimized ? 'chevron-up' : 'chevron-down'} 
                size={18} 
                color="rgba(255,255,255,0.6)" 
              />
            </TouchableOpacity>
          </View>
        </TouchableOpacity>

        {/* Expanded content */}
        {!isMinimized && (
          <ScrollView 
            style={styles.queriesList}
            showsVerticalScrollIndicator={false}
          >
            {activeQueries.map((query, index) => (
              <View 
                key={query.id} 
                style={[
                  styles.queryCard,
                  index === activeQueries.length - 1 && { marginBottom: 8 }
                ]}
              >
                {/* Query header */}
                <View style={styles.queryHeader}>
                  <View style={styles.queryTitleRow}>
                    <Ionicons 
                      name={getStatusIcon(query.status)} 
                      size={16} 
                      color={getStatusColor(query.status)} 
                    />
                    <Text style={styles.queryPrompt} numberOfLines={1}>
                      {query.prompt || 'Query'}
                    </Text>
                  </View>
                  
                  <TouchableOpacity 
                    onPress={() => handleCancelOrRemove(query.id)}
                    style={styles.closeButton}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons name="close" size={16} color="rgba(255,255,255,0.5)" />
                  </TouchableOpacity>
                </View>

                {/* Progress bar - animated */}
                {(query.status === QueryStatus.PENDING || query.status === QueryStatus.PROCESSING) && (
                  <View style={styles.progressContainer}>
                    <AnimatedProgressBar 
                      progress={query.progress || 0} 
                      isProcessing={query.status === QueryStatus.PROCESSING}
                    />
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <Text style={styles.progressText}>
                          {query.progressMessage || 'Querying odds data...'}
                        </Text>
                        {query.startTime && (
                          <ElapsedTimeDisplay startTime={query.startTime} />
                        )}
                      </View>
                      <TouchableOpacity 
                        onPress={() => setConfirmCancelId(query.id)}
                        style={{ paddingVertical: 2, paddingHorizontal: 6 }}
                      >
                        <Text style={{ color: '#f87171', fontSize: 11, fontWeight: '600' }}>Cancel</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}

                {/* Results / Actions */}
                {query.status === QueryStatus.COMPLETED && (
                  <View style={styles.resultsRow}>
                    <Text style={styles.resultsText}>
                      {query.sgpCount > 0 
                        ? `${query.sgpCount} SGPs` 
                        : `${query.results?.length || 0} results`}
                      {query.cached && ' • Cached'}
                      {query.durationMs && ` • ${(query.durationMs / 1000).toFixed(1)}s`}
                    </Text>
                    <TouchableOpacity 
                      style={styles.viewButton}
                      onPress={() => handleViewResults(query)}
                    >
                      <Text style={styles.viewButtonText}>View</Text>
                      <Ionicons name="arrow-forward" size={14} color="#fff" />
                    </TouchableOpacity>
                  </View>
                )}

                {/* Error state */}
                {query.status === QueryStatus.ERROR && (
                  <View style={styles.errorRow}>
                    <Ionicons name="warning" size={14} color="#f87171" />
                    <Text style={styles.errorText} numberOfLines={1}>
                      {query.error || 'Query failed'}
                    </Text>
                  </View>
                )}

                {/* Cancelled state */}
                {query.status === QueryStatus.CANCELLED && (
                  <Text style={styles.cancelledText}>Query cancelled</Text>
                )}
              </View>
            ))}

            {/* Clear all button */}
            {activeQueries.some(q => 
              q.status === QueryStatus.COMPLETED || 
              q.status === QueryStatus.ERROR || 
              q.status === QueryStatus.CANCELLED
            ) && (
              <TouchableOpacity 
                style={styles.clearAllButton}
                onPress={clearCompletedQueries}
              >
                <Text style={styles.clearAllText}>Clear Completed</Text>
              </TouchableOpacity>
            )}
          </ScrollView>
        )}
      </Animated.View>

      {/* Cancel Confirmation Modal - Use conditional render for better web support */}
      {confirmCancelId !== null && (
        <Modal
          visible={true}
          transparent
          animationType="fade"
          onRequestClose={dismissCancelModal}
        >
          <TouchableOpacity 
            style={styles.modalOverlay} 
            activeOpacity={1} 
            onPress={dismissCancelModal}
          >
            <TouchableOpacity 
              activeOpacity={1} 
              onPress={(e) => e.stopPropagation()}
            >
              <View style={styles.confirmModal}>
                <Text style={styles.confirmTitle}>Cancel Query?</Text>
                <Text style={styles.confirmMessage}>
                  Are you sure you want to cancel this query? Progress will be lost.
                </Text>
                <View style={styles.confirmButtons}>
                  <TouchableOpacity 
                    style={[styles.confirmButton, styles.confirmButtonCancel]}
                    onPress={dismissCancelModal}
                  >
                    <Text style={styles.confirmButtonText}>Keep Running</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={[styles.confirmButton, styles.confirmButtonConfirm]}
                    onPress={confirmCancel}
                  >
                    <Text style={[styles.confirmButtonText, { color: '#f87171' }]}>Cancel Query</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    bottom: 24,
    borderRadius: 16,
    overflow: 'hidden', // Clip content to container bounds
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.4,
    shadowRadius: 24,
    elevation: 20,
    zIndex: 9999,
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.3)',
    maxWidth: 'calc(100vw - 48px)', // Prevent bleeding off screen
    ...(Platform.OS === 'web' && {
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(139, 92, 246, 0.2)'
    })
  },
  // Mobile floating indicator
  mobileIndicator: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    bottom: 24,
    zIndex: 9999,
  },
  mobileIndicatorButton: {
    width: 56,
    height: 56,
    borderRadius: 28,
    overflow: 'hidden',
    shadowColor: '#8b5cf6',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 12,
    elevation: 10,
  },
  mobileIndicatorGradient: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileIndicatorBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#f87171',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
    borderWidth: 2,
    borderColor: '#1e1e2e',
  },
  processingRing: {
    position: 'absolute',
    width: 56,
    height: 56,
    borderRadius: 28,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
  },
  // Mobile bottom sheet
  mobileSheetOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'flex-end',
  },
  mobileSheet: {
    backgroundColor: '#1e1e2e',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: SCREEN_HEIGHT * 0.7,
    paddingBottom: 34, // Safe area padding
  },
  mobileSheetHandle: {
    width: 40,
    height: 4,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 8,
  },
  mobileSheetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  mobileSheetTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
  },
  mobileSheetContent: {
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  blurBackground: {
    ...StyleSheet.absoluteFillObject,
  },
  gradientOverlay: {
    ...StyleSheet.absoluteFillObject,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 14,
    minHeight: 52,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  positionButton: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.3)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusDot: {
    width: 24,
    height: 24,
    borderRadius: 12,
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#f87171',
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  badgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
  },
  chevronButton: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  queriesList: {
    maxHeight: 320,
    paddingHorizontal: 12,
  },
  queryCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
  },
  queryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  queryTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  queryPrompt: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '500',
    flex: 1,
  },
  closeButton: {
    padding: 4,
  },
  progressContainer: {
    marginTop: 4,
  },
  progressBar: {
    height: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  progressText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 11,
    marginTop: 6,
  },
  resultsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  resultsText: {
    color: '#4ade80',
    fontSize: 12,
    fontWeight: '500',
  },
  viewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(139, 92, 246, 0.4)',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  viewButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  errorText: {
    color: '#f87171',
    fontSize: 12,
    flex: 1,
  },
  cancelledText: {
    color: '#94a3b8',
    fontSize: 12,
    marginTop: 4,
  },
  clearAllButton: {
    alignItems: 'center',
    paddingVertical: 10,
    marginTop: 4,
    marginBottom: 8,
  },
  clearAllText: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 12,
    textDecorationLine: 'underline',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmModal: {
    backgroundColor: '#1e1e2e',
    borderRadius: 16,
    padding: 24,
    marginHorizontal: 32,
    maxWidth: 400,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  confirmTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  confirmMessage: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 24,
  },
  confirmButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  confirmButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  confirmButtonCancel: {
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.4)',
  },
  confirmButtonConfirm: {
    backgroundColor: 'rgba(248, 113, 113, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(248, 113, 113, 0.3)',
  },
  confirmButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default BackgroundQueryModal;

